import { z } from "zod";

// Base response schema with Zod validation
export const BaseRespSchema = z.object({
  success: z.boolean(),
  status: z.number(),
  code: z.string(),
  message: z.string(),
  timestamp: z.string().datetime(),
});
export type BaseResp = z.infer<typeof BaseRespSchema>;

// Generic response schema for data
export const ApiRespSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  BaseRespSchema.extend({
    data: dataSchema.optional(),
    error: z.string().optional(),
  });
export type ApiResp<T> = BaseResp & {
  data?: T;
  error?: string;
};

// Error response schema
export const ErrorRespSchema = BaseRespSchema.extend({
  error: z.string(),
  details: z.record(z.any()).optional(),
  stack: z.string().optional(),
});
export type ErrorResp = z.infer<typeof ErrorRespSchema>;

/* ------------------------------------------------------------- */
/* Pagination */
/* ------------------------------------------------------------- */
export const PageReqSchema = z.object({
  page: z.number().default(1),
  limit: z.number().default(10),
});
export type PageReq = z.infer<typeof PageReqSchema>;

export const PageMetaSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  totalPages: z.number(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
});
export type PageMeta = z.infer<typeof PageMetaSchema>;

export const PageDataSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({ data: z.array(dataSchema), meta: PageMetaSchema });
export type PageData<T> = {
  data: T[];
  meta: PageMeta;
};

export const PageRespSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  ApiRespSchema(PageDataSchema(dataSchema));
export type PageResp<T> = ApiResp<PageData<T>>;

/* ------------------------------------------------------------- */
/* Helper functions */
/* ------------------------------------------------------------- */

// Response status codes enum
export enum ResponseStatus {
  SUCCESS = 200,
  CREATED = 201,
  NO_CONTENT = 204,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  CONFLICT = 409,
  UNPROCESSABLE_ENTITY = 422,
  TOO_MANY_REQUESTS = 429,
  INTERNAL_SERVER_ERROR = 500,
  SERVICE_UNAVAILABLE = 503,
}

// Response codes enum
export enum ResponseCode {
  SUCCESS = "SUCCESS",
  CREATED = "CREATED",
  UPDATED = "UPDATED",
  DELETED = "DELETED",
  VALIDATION_ERROR = "VALIDATION_ERROR",
  NOT_FOUND = "NOT_FOUND",
  UNAUTHORIZED = "UNAUTHORIZED",
  FORBIDDEN = "FORBIDDEN",
  CONFLICT = "CONFLICT",
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
  INTERNAL_ERROR = "INTERNAL_ERROR",
  SERVICE_UNAVAILABLE = "SERVICE_UNAVAILABLE",
}

// Helper functions for creating responses
export const createSuccessResponse = <T>(
  data: T,
  message?: string,
  code?: ResponseCode
): ApiResp<T> => ({
  success: true,
  status: ResponseStatus.SUCCESS,
  code: code || ResponseCode.SUCCESS,
  message: message || "Operation completed successfully",
  data,
  timestamp: new Date().toISOString(),
});

export const createErrorResponse = (
  error: string,
  status: ResponseStatus = ResponseStatus.BAD_REQUEST,
  code?: ResponseCode,
  details?: Record<string, any>
): ErrorResp => ({
  success: false,
  status,
  code: code || ResponseCode.VALIDATION_ERROR,
  message: error,
  error,
  details,
  timestamp: new Date().toISOString(),
});

export const createPageData = <T>(data: T[], meta: PageMeta): PageData<T> => ({
  data,
  meta,
});

export const SearchRedirectSchema = z.object({
  redirectUrl: z.string().url().optional(),
});
export type SearchRedirect = z.infer<typeof SearchRedirectSchema>;
